import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Bike, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import Logo from "../components/Logo";
import { useForceDarkTheme } from "../lib/theme";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Jun-2026 (User-Wunsch) — E-Mail-Bestätigungsseite.
 * Liest ?token aus dem Link der Willkommens-Mail und ruft POST /auth/verify-email.
 */
export default function VerifyEmailPage() {
    useForceDarkTheme();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [state, setState] = useState("loading"); // loading | success | error
    const [message, setMessage] = useState("");
    const { refresh } = useAuth();
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;
        if (!token) {
            setState("error");
            setMessage("Kein Bestätigungstoken gefunden.");
            return;
        }
        (async () => {
            try {
                const { data } = await api.post("/auth/verify-email", { token });
                setState("success");
                setMessage(data?.message || "E-Mail erfolgreich bestätigt.");
                try { await refresh(); } catch { /* ignore */ }
            } catch (e) {
                setState("error");
                setMessage(e.response?.data?.detail || "Bestätigung fehlgeschlagen.");
            }
        })();
    }, [token, refresh]);

    return (
        <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12" data-testid="verify-email-page">
            <div className="w-full max-w-md text-center">
                <Link to="/" className="flex items-center gap-2 justify-center mb-8">
                    <Logo className="contents" boxClass="w-12 h-12" iconClass="w-7 h-7" deClass="text-black/40" />
                </Link>
                <div className="bg-white border border-gray-200 rounded-sm p-8">
                    {state === "loading" && (
                        <div data-testid="verify-email-loading">
                            <Loader2 className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
                            <h1 className="font-display text-2xl font-black mb-1">E-Mail wird bestätigt …</h1>
                        </div>
                    )}
                    {state === "success" && (
                        <div data-testid="verify-email-success">
                            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                            <h1 className="font-display text-2xl font-black mb-1">E-Mail bestätigt</h1>
                            <p className="text-sm text-gray-600 mb-6">{message}</p>
                            <Link to="/dashboard" className="inline-block bg-[#111827] hover:bg-black text-white font-bold uppercase tracking-wider px-6 py-3 rounded-sm" data-testid="verify-email-to-dashboard">
                                Zum Dashboard
                            </Link>
                        </div>
                    )}
                    {state === "error" && (
                        <div data-testid="verify-email-error">
                            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                            <h1 className="font-display text-2xl font-black mb-1">Bestätigung fehlgeschlagen</h1>
                            <p className="text-sm text-gray-600 mb-6">{message}</p>
                            <Link to="/dashboard" className="inline-block bg-[#111827] hover:bg-black text-white font-bold uppercase tracking-wider px-6 py-3 rounded-sm" data-testid="verify-email-to-dashboard">
                                Zum Dashboard
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
